import { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  User, Calendar, MapPin, FileText, Building2,
  Phone, Loader2, Clock, Activity, Stethoscope, Eye,
} from 'lucide-react';
import { STATO_PRATICA_LABELS, mapStatoToLifecycle } from '@/data/mockLifecycleData';
import PatientRecordModal from '@/components/PatientRecordModal';

function Cell({ icon: Icon, label, children }) {
  return (
    <div className="flex items-start gap-2.5">
      {Icon && <Icon className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0 mt-0.5" />}
      <div className="min-w-0">
        <p className="text-[10px] text-muted-foreground leading-none">{label}</p>
        <div className="mt-1 text-xs font-medium text-foreground leading-snug">{children}</div>
      </div>
    </div>
  );
}

export default function PatientPanelHeader({
  patient,
  onCall,
  isCalling,
  onViewMore,
  assignedProvider,
}) {
  const [isRecordOpen, setIsRecordOpen] = useState(false);
  const isMale = patient.sesso === 'M';
  const statoPratica = mapStatoToLifecycle(patient.stato);
  const statoLabel = STATO_PRATICA_LABELS[statoPratica] || patient.stato;

  const handleViewMore = () => {
    setIsRecordOpen(true);
    if (onViewMore) onViewMore();
  };

  return (
    <>
      <div className="bg-card rounded-xl border border-border shadow-card px-7 py-6" data-testid="patient-panel-header">
        <div className="flex items-start gap-7">

          {/* Avatar */}
          <div className={`w-16 h-16 rounded-full flex items-center justify-center flex-shrink-0 ${isMale ? 'bg-blue-100' : 'bg-pink-100'}`} data-testid="header-identity">
            <User className={`w-7 h-7 ${isMale ? 'text-blue-500' : 'text-pink-500'}`} />
          </div>

          {/* Grid 3 colonne */}
          <div className="flex-1 min-w-0 grid grid-cols-3 gap-x-8" data-testid="header-grid">

            {/* COL 1: Anagrafica base */}
            <div className="space-y-3">
              <div>
                <p className="text-sm font-semibold text-foreground">{patient.cognome} {patient.nome}</p>
                <Badge variant="chip" className="text-[9px] mt-1 font-mono">{patient.codiceFiscale}</Badge>
              </div>
              <Cell icon={User} label="Eta e Sesso">{patient.eta} anni &middot; {isMale ? 'Maschio' : 'Femmina'}</Cell>
              <Cell icon={Calendar} label="Data di Nascita">{patient.dataNascita}</Cell>
            </div>

            {/* COL 2: Clinica e Origine */}
            <div className="space-y-3">
              <Cell icon={Stethoscope} label="Diagnosi principale">{patient.diagnosiPrincipale || 'N/D'}</Cell>
              <Cell icon={Stethoscope} label="Diagnosi concomitante">{patient.diagnosiSecondaria || 'Nessuna'}</Cell>
              <Cell icon={MapPin} label="Provenienza">{patient.provenienza || 'PUA'}</Cell>
            </div>

            {/* COL 3: Amministrativa e Stato */}
            <div className="space-y-3">
              <Cell icon={Building2} label="Distretto">{patient.distretto?.replace('ASL Roma 2 - ', '') || 'N/D'}</Cell>
              <Cell icon={FileText} label="Codice Episodio">{patient.codiceEpisodio || `${10000 + patient.id}`}</Cell>
              <Cell icon={Activity} label="Stato ADI">
                {assignedProvider ? (
                  <Badge className="text-[9px] bg-warning/10 text-warning border border-warning/30 gap-1">
                    <Clock className="w-2.5 h-2.5" />In attesa di Provider
                  </Badge>
                ) : (
                  <Badge variant="outline" className="text-[9px]">{statoLabel}</Badge>
                )}
              </Cell>
            </div>
          </div>

          {/* Actions */}
          <div className="flex flex-col gap-3 justify-center items-end ml-auto" data-testid="header-actions">
            <button
              onClick={onCall}
              disabled={isCalling}
              className="flex items-center gap-2 px-4 py-2 border border-green-600 text-green-700 dark:text-green-500 rounded-lg text-sm font-medium hover:bg-green-50 dark:hover:bg-green-900/20 transition-colors disabled:opacity-50"
              data-testid="header-call-btn"
            >
              {isCalling ? <Loader2 className="w-4 h-4 animate-spin" /> : <Phone size={16} />}
              {isCalling ? 'Chiamata...' : 'Chiama Paziente'}
            </button>
            <button
              onClick={handleViewMore}
              className="flex items-center gap-2 px-4 py-2 border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 rounded-lg text-sm font-medium hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
              data-testid="header-view-more-btn"
            >
              <Eye size={16} /> Scheda paziente
            </button>
          </div>
        </div>
      </div>

      {/* Modale Scheda Paziente — renderizzata qui per aggancio globale */}
      <PatientRecordModal
        open={isRecordOpen}
        onClose={() => setIsRecordOpen(false)}
        patient={patient}
      />
    </>
  );
}
